import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormArray, FormGroup, ValidatorFn } from '@angular/forms';
import { FieldTypeConfig, FormlyFieldConfig } from '@ngx-formly/core';
import { FieldType } from '@ngx-formly/material';
import { FormlyFieldProps } from '@ngx-formly/material/form-field';
import {
  castArray,
  cloneDeep,
  defaultsDeep,
  filter,
  forEach,
  get,
  isNil,
  isNumber,
  map,
  merge,
  reduce,
  reject,
  round,
  some,
  sum,
  toNumber,
  zip,
} from 'lodash';
import {
  asyncScheduler,
  filter as rxFilter,
  map as rxMap,
  Observable,
  Subject,
  takeUntil,
} from 'rxjs';

export interface TableRow {
  id: string;
  label: string;
  fields: FormlyFieldConfig[];
  spanAcross?: boolean;
  tooltip?: string;
  hidden?: boolean;
  group?: TableRowGroup;
  color?: string;
  isTitle?: boolean;
  omit?: boolean;
}

interface TableRowGroup {
  first?: boolean;
  middle?: boolean;
  last?: boolean;
}

interface Summary {
  label: string;
  mobileLabels?: string[];
  addonLeft: string[];
  addonRight: string[];
  decimalFormat: string | string[];
  operator: string | string[];
  validations: SummaryValidations;
  __totals: number[];
  hintLabel?: string;
}

interface SummaryValidations {
  equal?: Array<number> | Array<[number, number]>;
  eq?: Array<number> | Array<[number, number]>;
  lt?: Array<number> | Array<[number, number]>;
  gt?: Array<number> | Array<[number, number]>;
  modelEq?: string[] | [string, number][];
  combinedSumEq?: number;
}

interface BulkSummary {
  label: string;
  description: string;
  mobileLabels: string[];
  path: string;
  __totals: number[];
}

interface InputProps extends FormlyFieldProps {
  tableRows: TableRow[];
  tableColumns?: string[];
  mobileLabels?: string[];
  commonRowFieldsConfig: Partial<FormlyFieldConfig>;
  summary: Summary;
  bulkSummary?: BulkSummary;
  addonLeft: string;
  addonRight: string;
  clearInputOnFocus: boolean;
  listenToExternalChanges: boolean;
  fullWidthLabel: boolean;
  wideLabel: boolean;
  compactLabel: boolean;
  condensed?: boolean;
  forceMobileView: boolean;
  readonly: boolean;
}

@Component({
    selector: 'lockton-apps-calc-table-type',
    templateUrl: './calc-table-type.component.html',
    styleUrls: ['./calc-table-type.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CalcTableTypeComponent
  extends FieldType<FieldTypeConfig<InputProps>>
  implements OnInit, AfterViewInit, OnDestroy
{
  internalModel$!: Observable<any>;
  readonly destroyer$: Subject<void> = new Subject();
  readonly internalForm: FormArray = new FormArray<any>([]);
  private groups!: FormlyFieldConfig[][];
  private defaultValidationPrecision = 2;

  get hasErrors(): boolean {
    return this.formControl.dirty && !!this.formControl.errors;
  }

  get totals(): number[] {
    return get(
      this.options.formState.derivedValues,
      `${this.key}.totals`,
      [0, 0]
    );
  }

  get bulkTotals(): number[] {
    return get(
      this.options.formState.derivedValues,
      this.props.bulkSummary?.path as string,
      [0, 0]
    );
  }

  private get formattedInternalFormValue(): unknown {
    return merge({}, ...this.internalForm.value);
  }

  ngOnInit(): void {
    this.setGroups(this.props.tableRows);
    this.initializeForm(
      this.props.summary?.validations,
      this.props.commonRowFieldsConfig
    );
    this.listenToExternalChanges();
  }

  ngAfterViewInit(): void {
    this.onModelChange(true);

    if (
      this.props.listenToExternalChanges &&
      this.field.options?.fieldChanges
    ) {
      this.internalModel$ = this.field.options.fieldChanges.pipe(
        rxFilter(
          ({ type, field: { key } }) =>
            type === 'expressionChanges' && key === this.key
        ),
        rxMap(() => {
          asyncScheduler.schedule(this.onModelChange.bind(this, true));

          return cloneDeep(get(this.model, this.key));
        })
      );
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();

    this.destroyer$.next();
    this.destroyer$.complete();
  }

  onModelChange(externalChange?: boolean): void {
    this.formControl.setValue(this.formattedInternalFormValue);
    if (this.props.summary) {
      this.updateTotals();
    }
    this.formControl.updateValueAndValidity();

    if (!externalChange) {
      this.formControl.markAsDirty();
    }
  }

  private setGroups(rows: TableRow[]): void {
    const cleanedRows: TableRow[] = filter(
      rows,
      ({ fields }) => !!fields.length
    );

    this.groups = zip(...map(cleanedRows, 'fields')) as FormlyFieldConfig[][];
  }

  private updateTotals(): void {
    if (this.options.formState.derivedValues) {
      this.options.formState.derivedValues[`${this.key}.totals`] =
        this.calculateTotals(this.formattedInternalFormValue);
    }
  }

  private initializeForm(
    validations: SummaryValidations,
    commonRowFieldsConfig: Partial<FormlyFieldConfig>
  ): void {
    const validatorFns: ValidatorFn[] = [
      () => (this.internalForm.invalid ? { invalidAncestors: true } : null),
    ];

    if (validations) {
      forEach(validations, (validationData, name: string) => {
        switch (name) {
          // NOTE: moving forward follow lodash naming conventions

          // equal is @deprecated use eq instead
          case 'eq':
          case 'equal': {
            // TODO: replace with input precision
            validatorFns.push(() => {
              if (!Array.isArray(validationData)) {
                return null;
              }
              const isIncorrect: boolean = some(this.totals, (total, index) => {
                const [validationValue, precision] = this.getValidationData(
                  validationData,
                  index
                );

                return (
                  validationData &&
                  !isNil(validationValue) &&
                  round(total, precision) !==
                    round(toNumber(validationValue), precision)
                );
              });

              // TODO:: update return once "equal" is not used any more
              return isIncorrect ? { [name]: true } : null;
            });
            break;
          }
          case 'lt':
            validatorFns.push(() => {
              if (!Array.isArray(validationData)) {
                return null;
              }
              const isIncorrect: boolean = some(this.totals, (total, index) => {
                const [validationValue, precision] = this.getValidationData(
                  validationData,
                  index
                );

                return (
                  validationData &&
                  !isNil(validationValue) &&
                  round(total, precision) > toNumber(validationValue)
                );
              });

              return isIncorrect ? { lt: true } : null;
            });
            break;

          // atLeast is @deprecated use gt instead
          case 'gt':
          case 'atLeast':
            validatorFns.push(() => {
              if (!Array.isArray(validationData)) {
                return null;
              }
              const isIncorrect: boolean = some(this.totals, (total, index) => {
                const [validationValue, precision] = this.getValidationData(
                  validationData,
                  index
                );

                return (
                  validationData &&
                  !isNil(validationValue) &&
                  round(total, precision) < toNumber(validationValue)
                );
              });

              return isIncorrect ? { gt: true } : null;
            });
            break;

          // sum of all groups
          case 'combinedSumEq': {
            validatorFns.push(() => {
              if (!validationData) {
                return null;
              }
              const [validationValue, precision] = this.getValidationData(
                [validationData as number],
                0
              );

              const isIncorrect =
                round(sum(this.totals), precision) !== validationValue;

              return isIncorrect ? { combinedSumEq: true } : null;
            });
            break;
          }

          case 'modelEq': {
            validatorFns.push(() => {
              if (!validationData) {
                return null;
              }
              if (!Array.isArray(validationData)) {
                return null;
              }

              const model = this.field.parent?.formControl?.value;

              const isIncorrect = some(this.totals, (total, index) => {
                const [validationPath, precision] = this.getValidationData(
                  validationData as string[] | [string, number][],
                  index
                );
                const path = validationPath;
                if (!path) {
                  return false;
                }
                const modelValue = round(toNumber(get(model, path)), precision);
                return modelValue !== round(total, precision);
              });

              return isIncorrect ? { modelEq: true } : null;
            });

            break;
          }
        }
      });
    }

    this.formControl.setValidators(validatorFns);

    if (some(validations, (_, name) => name === 'modelEq')) {
      this.field.parent?.formControl?.valueChanges
        .pipe(takeUntil(this.destroyer$))
        .subscribe(() => {
          this.formControl.updateValueAndValidity({ emitEvent: false });
        });
    }

    forEach(this.props.tableRows, (row: TableRow) => {
      this.internalForm.push(new FormGroup({}));

      forEach(row.fields, (field: FormlyFieldConfig) => {
        if (!field.key) {
          return;
        }

        defaultsDeep(field, commonRowFieldsConfig, {
          id: `${this.key}.${field.key}`,
          props: {
            clearOnFocus: this.props.clearInputOnFocus,
            readonly: this.props.readonly,
          },
        });

        if (this.formControl.defaultValue) {
          field.defaultValue = get(
            this.formControl.defaultValue,
            field.key as string,
            row.omit ? '' : 0
          );
        } else {
          if (!isNil(field.defaultValue)) return;

          field.defaultValue = get(
            this.field.defaultValue,
            field.key as string,
            0
          );
        }
      });
    });

    this.formControl.updateValueAndValidity();
  }

  private listenToExternalChanges(): void {
    if (!this.options.formState.validityCheck$) {
      return;
    }
    this.options.formState.validityCheck$
      .pipe(takeUntil(this.destroyer$))
      .subscribe(() => {
        this.internalForm.markAllAsTouched();
        this.formControl.markAsDirty();
      });
  }

  private calculateTotals(model: any): Array<number | null> {
    const operators = Array.isArray(this.props.summary.operator)
      ? this.props.summary.operator
      : map(this.groups, () => this.props.summary.operator);

    return map(this.groups, (fields: FormlyFieldConfig[], index) => {
      const values = map(
        reject(fields, { props: { omitCalculation: true } }),
        ({ key }) => get(model, key as string)
      );
      const operator = operators[index];

      if (operator === '*') {
        return reduce(values, (x, y) => x * y);
      }
      if (operator === 'null') {
        return null;
      }
      return sum(filter(values, isNumber));
    });
  }

  private getValidationData(
    validationData:
      | string[]
      | number[]
      | [number, number][]
      | [string, number][],
    index: number
  ): [string | number, number] {
    const colValidationData = castArray(validationData[index]) as
      | [string]
      | [number]
      | [string, number]
      | [number, number];

    const validationValue = colValidationData[0];

    const precision = isNil(colValidationData[1])
      ? this.defaultValidationPrecision
      : colValidationData[1];

    return [validationValue, precision];
  }
}
